import { Card, Flex, type GlobalToken, Typography, theme } from "antd";
import type React from "react";
import { useLayoutEffect, useRef, useState } from "react";

export interface KanbanColumn<TKey extends string> {
	key: TKey;
	title: React.ReactNode;
	/** Final state (e.g. completed) — the column gets a striped background. */
	terminal?: boolean;
	/** Bad outcome (e.g. rejected) — the column is tinted light red. */
	danger?: boolean;
}

export interface KanbanConfig<TData, TKey extends string> {
	/** Board columns, left to right. Rows whose key matches no column are not shown. */
	columns: readonly KanbanColumn<TKey>[];
	/** Which column a row belongs to (e.g. its status). */
	getColumnKey: (row: TData) => TKey;
	getRowKey: (row: TData) => string;
	renderCard: (row: TData) => React.ReactNode;
}

export interface KanbanBoardProps<TData, TKey extends string> {
	rows: TData[];
	footer: React.ReactNode;
	config: KanbanConfig<TData, TKey>;
	onCardClick?: (row: TData) => void;
}

/** Light fill for a column; striped when terminal, red-tinted when danger. */
function columnBackground<TKey extends string>(
	column: KanbanColumn<TKey>,
	token: GlobalToken,
): string {
	const [light, dark] = column.danger
		? [
				`color-mix(in srgb, ${token.colorError} 4%, transparent)`,
				`color-mix(in srgb, ${token.colorError} 8%, transparent)`,
			]
		: [token.colorFillQuaternary, token.colorFillTertiary];
	return column.terminal
		? `repeating-linear-gradient(135deg, ${light} 0 8px, ${dark} 8px 16px)`
		: light;
}

/** Board of columns (one per key), each listing its rows as cards. Fills the viewport height; drag to scroll sideways. */
export function KanbanBoard<TData, TKey extends string>({
	rows,
	footer,
	config,
	onCardClick,
}: KanbanBoardProps<TData, TKey>) {
	const { token } = theme.useToken();
	// The page wrappers above don't pass a height down, so size the board from its own
	// top edge to the bottom of the viewport.
	// ponytail: measured on mount/resize only; observe layout shifts if content above it changes height.
	const ref = useRef<HTMLDivElement>(null);
	const [top, setTop] = useState(0);
	// Grab-to-scroll horizontally (no scrollbar). `moved` outlives pointerup so the
	// click that ends a drag is swallowed instead of opening a card.
	const drag = useRef({ active: false, moved: false, x: 0, left: 0 });
	const [dragging, setDragging] = useState(false);
	useLayoutEffect(() => {
		const update = () => setTop(ref.current?.getBoundingClientRect().top ?? 0);
		update();
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, []);

	return (
		<Flex
			ref={ref}
			vertical
			gap={8}
			style={{
				width: "100%",
				// 20px = the layout Content's bottom padding.
				height: `calc(100dvh - ${top}px - 20px)`,
				minHeight: 300,
			}}
		>
			<Flex
				gap={12}
				onPointerDown={(e) => {
					if (e.button !== 0) return;
					drag.current = {
						active: true,
						moved: false,
						x: e.clientX,
						left: e.currentTarget.scrollLeft,
					};
				}}
				onPointerMove={(e) => {
					const d = drag.current;
					if (!d.active) return;
					const dx = e.clientX - d.x;
					if (!d.moved && Math.abs(dx) > 4) {
						d.moved = true;
						setDragging(true);
						e.currentTarget.setPointerCapture(e.pointerId);
					}
					if (d.moved) e.currentTarget.scrollLeft = d.left - dx;
				}}
				onPointerUp={() => {
					drag.current.active = false;
					setDragging(false);
				}}
				onPointerCancel={() => {
					drag.current.active = false;
					setDragging(false);
				}}
				onClickCapture={(e) => {
					if (drag.current.moved) e.stopPropagation();
				}}
				style={{
					flex: 1,
					minHeight: 0,
					overflowX: "auto",
					scrollbarWidth: "none",
					cursor: dragging ? "grabbing" : "grab",
					userSelect: dragging ? "none" : undefined,
				}}
			>
				{config.columns.map((column) => {
					const columnRows = rows.filter(
						(row) => config.getColumnKey(row) === column.key,
					);
					return (
						<Flex
							key={column.key}
							vertical
							gap={8}
							style={{
								// Explicit width (not just a flex basis) so long card content cannot widen a column.
								flex: "none",
								width: 400,
								minHeight: 0,
								padding: `${token.paddingXS}px 0`,
								borderRadius: token.borderRadius,
								background: columnBackground(column, token),
							}}
						>
							<Flex
								justify="space-between"
								align="center"
								style={{ padding: `0 ${token.paddingSM}px` }}
							>
								{column.title}
								<Typography.Text type="secondary">
									{columnRows.length}
								</Typography.Text>
							</Flex>
							<Flex
								vertical
								gap={8}
								style={{
									flex: 1,
									overflowY: "auto",
									scrollbarWidth: "none",
									// Room inside the scroll box so the hoverable cards' shadow isn't clipped.
									padding: `4px ${token.paddingSM}px`,
								}}
							>
								{columnRows.map((row) => (
									<Card
										style={{ flexShrink: 0 }}
										key={config.getRowKey(row)}
										size="small"
										hoverable={!!onCardClick}
										onClick={() => onCardClick?.(row)}
									>
										{config.renderCard(row)}
									</Card>
								))}
							</Flex>
						</Flex>
					);
				})}
			</Flex>
			{footer}
		</Flex>
	);
}
